import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import Button from '../../ui/Button';
import { Dialog } from '../accounts/AccountBits';
import nurseryCounterService from '../../../services/nurseryCounter';
import { errorMessage } from '../../../utils/apiResponse';
import { money } from '../../../utils/nurseryMoney';
import type { OrderRefund } from '../../../types/nurseryAccounts';
import type { TillSession } from '../../../types/nurseryCounter';

/**
 * Hand a cash refund back from a till: an order paid in cash at the counter
 * and picked short, or cancelled. The till records it as a refund.
 */
export function CashRefundDialog({ refund, onClose, onDone }: {
  refund: OrderRefund;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'CashRefundDialog' });
  const [tills, setTills] = useState<TillSession[] | null>(null);
  const [sessionId, setSessionId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    nurseryCounterService.listTills().then((res) => {
      if (cancelled) return;
      const open = res.success ? res.data.open : [];
      setTills(open);
      if (open.length === 1) setSessionId(open[0].id);
    }).catch((e) => { if (!cancelled) { setTills([]); setError(errorMessage(e, t('tillsFailed'))); } });
    return () => { cancelled = true; };
  }, [t]);

  const pay = async () => {
    if (!sessionId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.payCashRefund(refund.id, sessionId);
      if (res.success) {
        onDone();
        return;
      }
      setError(errorMessage(res, t('recordFailed')));
    } catch (e) {
      setError(errorMessage(e, t('recordFailed')));
    }
    setBusy(false);
  };

  return (
    <Dialog
      title={t('title', { amount: money(refund.amount) })}
      subtitle={t('subtitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={pay} disabled={!sessionId || busy}>{busy ? t('recording') : t('givenBack')}</Button>
        </>
      }
    >
      {tills === null ? (
        <div className="h-10 bg-neutral-100 rounded animate-pulse" />
      ) : tills.length === 0 ? (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 break-words">
          <Trans t={t} i18nKey="noTill" components={{ link: <Link to="/nursery/tills" className="underline font-medium" /> }} />
        </p>
      ) : (
        <div>
          <label htmlFor="refund-till" className="block text-sm font-medium text-neutral-700 mb-1">{t('fromTill')}</label>
          <select id="refund-till" value={sessionId} onChange={(e) => setSessionId(e.target.value ? Number(e.target.value) : '')}
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm">
            {tills.length > 1 && <option value="">{t('chooseTill')}</option>}
            {tills.map((till) => (
              <option key={till.id} value={till.id}>
                {till.till_name}{till.totals ? t('holds', { amount: money(till.totals.expected_cash) }) : ''}
              </option>
            ))}
          </select>
        </div>
      )}
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}
